import { emailDomainSuggestions, tryParseMoney } from '@sitzmark/core';
import { CardAttention } from './card-attention.js';
import { TerminalCardPanel, useCardAttempt, useRegisterCards } from '../register/card-terminal.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Loading,
  Modal,
  Mono,
  Screen,
  SectionNav,
} from '../../components/ui.js';
import { useNavReset } from '../../components/nav-reset.js';
import { useNotice } from '../../components/notice.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { TENDER_LABELS } from '../../lib/tender.js';
import { useSearchTyping } from '../../lib/typing-focus.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import { customerName } from '../customers/customer.js';
import { NewCustomerDialog } from '../customers/new-customer.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { useReceiptPrinter, useReceiptPrinting } from './receipt.js';
import './sales.css';

/**
 * Sales history and receipts (SCOPE §8.6, §9.1).
 *
 * One search box covering receipt number, customer and item, because staff at a
 * counter should not have to know which field their scrap of information lives
 * in (§2.2-3). A scanned receipt barcode is the sale number, so scanning lands
 * straight on the sale.
 *
 * This is also the front door to returns: a return starts by finding the
 * original sale (§8.5).
 */

/**
 * A refund is a completed transaction, but calling it "Paid" reads as though
 * the customer handed money over. It is its own kind of entry.
 */
function statusBadge(
  status: string,
  isRefund: boolean,
): {
  tone: 'success' | 'danger' | 'warning' | 'neutral';
  label: string;
} {
  if (isRefund) return { tone: 'danger', label: 'Refund' };
  if (status === 'completed') return { tone: 'success', label: 'Paid' };
  // Still being paid for, and the goods are held rather than sold — so it is
  // neither "Paid" nor an unfinished draft (§8.3).
  if (status === 'layaway') return { tone: 'warning', label: 'Layaway' };
  // Paid on the web shop and not collected or shipped yet (decision 156): the
  // money is taken, the goods have not left — Online orders is where it moves.
  if (status === 'ordered') return { tone: 'warning', label: 'Online order' };
  if (status === 'refunded') return { tone: 'danger', label: 'Refunded' };
  if (status === 'partially_refunded') return { tone: 'warning', label: 'Part refunded' };
  // The only voided sales this list shows are canceled layaways (FB-1088).
  if (status === 'voided') return { tone: 'neutral', label: 'Layaway canceled' };
  return { tone: 'neutral', label: status };
}

/**
 * The way to the web shop's orders, with the work waiting on it — the same
 * count the Sales tab carries, from the same query, so the two never disagree.
 */
function OnlineOrdersButton({ onOpen }: { onOpen: () => void }) {
  const waiting = useQuery({
    queryKey: ['onlineOrders', 'count'],
    queryFn: () => api.onlineOrders.count.query(),
  }).data;
  return (
    <Button variant="secondary" onClick={onOpen}>
      Online orders
      {waiting !== undefined && waiting > 0 ? (
        <>
          {' '}
          <Badge tone="warning">{waiting} to do</Badge>
        </>
      ) : null}
    </Button>
  );
}

export function SalesScreen({
  onReturnStarted,
  onOpenCustomer,
  onOpenOnline,
}: {
  onReturnStarted: () => void;
  /** Hand the customer to the Customers screen, which owns their history. */
  onOpenCustomer: (customerId: string) => void;
  /**
   * The web shop's orders, worked from here (decision 159). Absent for anyone
   * who cannot work them, and then the button is not on screen (§2.2-7).
   */
  onOpenOnline?: () => void;
}) {
  const [term, setTerm] = useState('');
  /** Typing anywhere here types into the search, as on the register. */
  const searchRef = useSearchTyping(setTerm);
  const [debounced, setDebounced] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** In the shop, online, or both (S5-13). */
  const [channel, setChannel] = useState<'all' | 'store' | 'web'>('all');

  // Pressing Sales while a receipt is open closes it (item 31): back to the
  // list, search kept — it is typed work.
  useNavReset(() => setSelectedId(null));

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 150);
    return () => clearTimeout(timer);
  }, [term]);

  const results = useQuery({
    queryKey: ['sales', 'search', debounced, channel],
    queryFn: () =>
      api.sales.search.query({ term: debounced === '' ? undefined : debounced, channel }),
  });

  return (
    <Screen
      title="Sales"
      subtitle="Look up a receipt, reprint it, or take a return."
      context={
        onOpenOnline === undefined ? undefined : <OnlineOrdersButton onOpen={onOpenOnline} />
      }
    >
      <input
        ref={searchRef}
        className="search__input"
        placeholder="Scan a receipt, or search by number, customer or item…"
        value={term}
        autoFocus
        autoComplete="off"
        onChange={(event) => setTerm(event.target.value)}
      />

      {/* Card payments that need a person (decision 230): only when there are any. */}
      <CardAttention />

      <SectionNav
        label="Where it was sold"
        current={channel}
        items={[
          { key: 'all', label: 'All' },
          { key: 'store', label: 'In store' },
          { key: 'web', label: 'Online' },
        ]}
        onChange={setChannel}
      />

      {results.isError ? <Callout tone="danger">{errorMessage(results.error)}</Callout> : null}

      <Card padded={false}>
        {results.isPending ? (
          <Loading label="Searching" />
        ) : (results.data?.length ?? 0) === 0 ? (
          <p className="text-secondary sales__empty">
            {debounced === '' ? 'No sales yet.' : `Nothing matches “${debounced}”.`}
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Receipt</th>
                <th>When</th>
                <th>Customer</th>
                <th className="num">Items</th>
                <th className="num">Paid</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {results.data?.map((sale) => {
                const badge = statusBadge(sale.status, sale.isRefund);
                return (
                  <tr key={sale.id}>
                    <td>
                      {/* An online order is known by its W- number before it has
                          a sale number — which, when it completes, is the same. */}
                      <Mono>{sale.number ?? sale.webOrderNumber ?? '—'}</Mono>{' '}
                      <Badge tone={badge.tone}>{badge.label}</Badge>
                    </td>
                    <td className="text-secondary">{new Date(sale.activityAt).toLocaleString()}</td>
                    <td>
                      {/*
                        "Who was this?" is nearly always followed by "what else
                        have they bought?" — so the name is the way through to
                        the answer rather than a dead label (§2.2-3).
                      */}
                      {sale.customerId !== null && sale.customerName !== null ? (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => onOpenCustomer(sale.customerId!)}
                        >
                          {sale.customerName}
                        </button>
                      ) : null}
                      {/* An online order matched by email to a customer on file
                          keeps the name typed at checkout; when the two differ,
                          both are said, so nobody wonders whose order it is. */}
                      {sale.orderedAs !== null &&
                      sale.orderedAs.trim().toLowerCase() !==
                        (sale.customerName ?? '').trim().toLowerCase() ? (
                        <div className="text-secondary">Ordered as {sale.orderedAs}</div>
                      ) : null}
                      {(sale.customerId === null || sale.customerName === null) &&
                      sale.orderedAs === null ? (
                        <span className="text-secondary">—</span>
                      ) : null}
                    </td>
                    <td className="num">{sale.itemCount}</td>
                    {/* What was collected, not the cash-price total — so the
                        list agrees with the receipt printed from it. */}
                    <td className="num money">
                      {money(sale.collectedCents)}
                      {/* On a layaway the collected figure is a deposit, not a
                          price — saying so is the difference between "$100" and
                          "$100 of $500" (§2.2-10). */}
                      {sale.status === 'layaway' && sale.balanceCents > 0 ? (
                        <div className="text-secondary label-micro">
                          {money(sale.balanceCents)} to go
                        </div>
                      ) : null}
                    </td>
                    <td className="num">
                      <Button variant="ghost" onClick={() => setSelectedId(sale.id)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {selectedId !== null ? (
        <ReceiptDialog
          saleId={selectedId}
          onClose={() => setSelectedId(null)}
          onOpenCustomer={onOpenCustomer}
          onReturnStarted={() => {
            setSelectedId(null);
            onReturnStarted();
          }}
        />
      ) : null}
    </Screen>
  );
}

// ---------------------------------------------------------------------------

/**
 * One receipt, with everything that can be done to it.
 *
 * Exported because a customer's purchase history needs the same dialog: "I
 * bought this here last month" is answered by finding the person, finding the
 * item, and opening the receipt it was on — without retyping a number.
 */
export function ReceiptDialog({
  saleId,
  onClose,
  onReturnStarted,
  onOpenCustomer,
}: {
  saleId: string;
  onClose: () => void;
  onReturnStarted: () => void;
  /**
   * Omitted when the receipt is already being read from that customer's own
   * profile — a link back to where you are is not a link (§2.2-13).
   */
  onOpenCustomer?: (customerId: string) => void;
}) {
  const [returning, setReturning] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [collecting, setCollecting] = useState(false);
  const [cancelingLayaway, setCancelingLayaway] = useState(false);

  /*
   * Which document this dialog is currently showing (SCOPE §8.5).
   *
   * An exchange is two receipts, and the credit line on either one is only
   * explicable by the other. Held here rather than pushed back up to the caller
   * because both callers — the sales list and a customer's history — want the
   * same behaviour, and neither should have to know that some receipts have a
   * second half.
   */
  const [viewingId, setViewingId] = useState(saleId);
  useEffect(() => setViewingId(saleId), [saleId]);

  const detail = useQuery({
    queryKey: ['sales', 'detail', viewingId],
    queryFn: () => api.sales.get.query({ saleId: viewingId }),
  });
  const settings = useReceiptPrinting();
  const printer = useReceiptPrinter();

  const data = detail.data;
  const canPrint = data !== undefined && settings !== null;
  const anythingReturnable = (data?.lines ?? []).some((line) => line.returnableQuantity > 0);

  if (returning && data !== undefined) {
    return (
      <ReturnDialog
        saleId={viewingId}
        saleNumber={data.sale.number}
        lines={data.lines}
        onClose={() => setReturning(false)}
        onStarted={onReturnStarted}
      />
    );
  }

  if (emailing && data !== undefined) {
    return (
      <EmailReceiptDialog
        saleId={viewingId}
        defaultEmail={data.customer?.email ?? ''}
        attachedCustomerId={data.customer?.id ?? null}
        onClose={() => setEmailing(false)}
      />
    );
  }

  if (collecting && data !== undefined) {
    return <LayawayPaymentDialog saleId={viewingId} onClose={() => setCollecting(false)} />;
  }

  if (cancelingLayaway && data !== undefined) {
    return <CancelLayawayDialog saleId={viewingId} onClose={() => setCancelingLayaway(false)} />;
  }

  return (
    <Modal
      open
      /* A receipt is a table of lines and tenders, not a question. */
      wide
      title={
        data === undefined
          ? 'Receipt'
          : `Receipt ${data.sale.number ?? data.sale.webOrderNumber ?? ''}`
      }
      description={
        data === undefined
          ? undefined
          : `${data.sale.registerName ?? 'Register'} · ${data.sale.employeeName ?? 'Unknown'} · ${
              data.sale.completedAt === null ? '' : new Date(data.sale.completedAt).toLocaleString()
            }`
      }
      onClose={onClose}
      secondaryAction={
        <>
          {/* Destructive, so kept apart from Take a payment (DESIGN-DNA). */}
          {data?.sale.status === 'layaway' ? (
            <Button variant="ghost" onClick={() => setCancelingLayaway(true)}>
              Cancel layaway
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="ghost"
            disabledReason={canPrint ? undefined : 'Loading the receipt'}
            onClick={() => setEmailing(true)}
          >
            Email
          </Button>
          <Button
            variant="ghost"
            disabledReason={canPrint ? undefined : 'Loading the receipt'}
            onClick={() => printer.print(data!, settings!, true)}
          >
            Gift receipt
          </Button>
          <Button
            variant="secondary"
            disabledReason={canPrint ? undefined : 'Loading the receipt'}
            onClick={() => printer.print(data!, settings!)}
          >
            Reprint
          </Button>
        </>
      }
      primaryAction={
        /*
         * A layaway is not finished, so the thing to do to it is collect the
         * next instalment — not return goods the customer has not had yet.
         * "Where do I take a payment?" was unanswerable from this screen, which
         * is the screen a customer's layaway is looked up on (§8.3).
         */
        data?.sale.status === 'layaway' ? (
          <Button variant="primary" onClick={() => setCollecting(true)}>
            Take a payment
          </Button>
        ) : (
          <Button
            variant="primary"
            disabledReason={
              data === undefined
                ? 'Loading'
                : !anythingReturnable
                  ? 'Everything on this sale has already been returned'
                  : undefined
            }
            onClick={() => setReturning(true)}
          >
            Return items
          </Button>
        )
      }
    >
      {printer.frame}

      {data === undefined ? (
        <Loading label="Loading the sale" />
      ) : (
        <div className="receipt">
          {/*
            The same figures the paper receipt carries, in the same basis
            (decision 39) — `data.receipt`, already resolved by the server to
            whichever price was actually charged.

            This screen used to render `sale.netSubtotalCents` and `line.netCents`
            directly, which are the *cash* figures, so a card sale showed cash
            prices above a card payment. Two renderings of one document had
            drifted apart; the paper one was corrected and this one was missed,
            and a browser test found it on its first real run. Both read from
            one place now.
          */}
          <table className="table">
            <tbody>
              {data.receipt.lines.map((line, index) => (
                <tr key={line.id}>
                  <td>
                    {line.description}
                    <div className="receipt__meta">
                      <Mono>{line.sku ?? '—'}</Mono>
                      {(data.lines[index]?.refundedQuantity ?? 0) > 0 ? (
                        <Badge tone="warning">{data.lines[index]!.refundedQuantity} returned</Badge>
                      ) : null}
                      {data.lines[index]?.priceOverridden === true ? (
                        <Badge tone="warning">Override</Badge>
                      ) : null}
                    </div>
                  </td>
                  <td className="num">
                    {line.quantity} × {money(line.unitPriceCents)}
                  </td>
                  <td className="num money">{money(line.netCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="receipt__totals">
            <Row label="Subtotal" value={money(data.receipt.subtotalCents)} />
            {data.receipt.discountCents !== 0 ? (
              <Row label="Discounts" value={money(-data.receipt.discountCents)} />
            ) : null}
            {data.receipt.taxes.map((tax) => (
              <Row key={tax.name} label={tax.name} value={money(tax.taxCents)} />
            ))}
            <Row label="Total" value={money(data.receipt.totalCents)} strong />
          </div>

          <div className="receipt__tenders">
            <span className="label-micro">Paid by</span>
            {data.tenders.map((tender) => (
              <div key={tender.id} className="receipt__tender">
                <span>
                  {/*
                    The one tender line that cannot be explained by the document
                    it is printed on: on a sale it is value that came from
                    somewhere, on a refund it is value that went somewhere. Both
                    answers are one press away rather than a search through the
                    day (§2.2-3, recognition over recall).
                  */}
                  {tender.kind === 'exchange_credit' && data.exchange !== null ? (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setViewingId(data.exchange!.id)}
                    >
                      {TENDER_LABELS[tender.kind]} ·{' '}
                      {data.exchange.isRefund ? 'from return' : 'spent on sale'}{' '}
                      {data.exchange.number ?? ''}
                    </button>
                  ) : (
                    <>
                      {TENDER_LABELS[tender.kind] ?? tender.kind}
                      {tender.cardLast4 !== null ? ` ····${tender.cardLast4}` : ''}
                    </>
                  )}
                  {tender.voidedAt !== null ? ' (refunded)' : ''}
                </span>
                <span className="money">{money(tender.amountCents)}</span>
              </div>
            ))}
          </div>

          {data.customer !== null ? (
            <p className="text-secondary">
              {onOpenCustomer === undefined ? (
                `${data.customer.firstName ?? ''} ${data.customer.lastName ?? ''}`.trim()
              ) : (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => onOpenCustomer(data.customer!.id)}
                >
                  {`${data.customer.firstName ?? ''} ${data.customer.lastName ?? ''}`.trim()}
                </button>
              )}
              {data.customer.phone !== null ? ` · ${data.customer.phone}` : ''}
            </p>
          ) : (
            /*
             * Rung up without asking, and the customer offers a phone number a
             * minute later. Without this the sale is theirs forever with no
             * name on it, and the purchase history that lets them return
             * without a receipt never gets the line (§9.1).
             *
             * Attribution only — see `sales.attachCustomer`. Nothing about the
             * money moves, and the dialog says so.
             */
            <AttachToCustomer
              saleId={data.sale.id}
              saleNumber={data.sale.number ?? data.sale.webOrderNumber ?? null}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`receipt__row${strong === true ? ' receipt__row--strong' : ''}`}>
      <span>{label}</span>
      <span className="money">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Take an instalment against a layaway (SCOPE §8.3).
 *
 * "Take payments against layaway at any register" is the client's requirement,
 * and the endpoint has always allowed it — but the only screen that shows a
 * customer's layaway offered nothing to press, so the answer to "she has come
 * in to pay some off" was that there wasn't one.
 *
 * The amount defaults to **the whole balance**, because paying a layaway off is
 * the commonest reason to walk in with it (§2.2-5, smart defaults), and typing
 * over it is one field.
 */
/**
 * Cancel a layaway and give the money back (SCOPE §8.3; 2026-09-25).
 *
 * Shows every deposit and instalment, the store's fee and the refund *before*
 * anyone commits — the server's figures, from the same calculation the cancel
 * uses. The refund goes back as cash out of this till's drawer, to the card,
 * or as store credit, and is recorded as such, so the drawer still balances.
 * A manager approves; the fee can be waived.
 */
function CancelLayawayDialog({ saleId, onClose }: { saleId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  // Through BlueSage, a card refund goes back to the cards it was paid on (decision 231).
  const cards = useRegisterCards();
  const [refundKind, setRefundKind] = useState<'cash' | 'card' | 'store_credit'>('cash');
  const [waiveFee, setWaiveFee] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);

  const layaway = useQuery({
    queryKey: ['register', 'layaway', saleId],
    queryFn: () => api.register.layaway.query({ saleId }),
  });
  const layawayId = layaway.data?.layaway.id ?? null;
  const quote = useQuery({
    queryKey: ['register', 'layaway', saleId, 'cancel-quote'],
    queryFn: () => api.register.layawayCancelQuote.query({ layawayId: layawayId! }),
    enabled: layawayId !== null,
  });
  const stations = useQuery({
    queryKey: ['register', 'stations'],
    queryFn: () => api.register.stations.query(),
  });
  const registerId = stations.data?.till.registerId ?? null;

  const cancel = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.register.cancelLayaway.mutate({
        layawayId: layawayId!,
        registerId: registerId!,
        refundKind,
        waiveFee,
        ...(override === undefined ? {} : { override }),
      }),
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
    onSuccess: () => {
      setNeedsApproval(false);
      recordBreadcrumb('sales', 'canceled a layaway');
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['register'] });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  // The refund with the fee or without it — both the server's figures.
  const refundCents =
    quote.data === undefined ? 0 : waiveFee ? quote.data.paidCents : quote.data.refundCents;

  if (cancel.data !== undefined) {
    const handed = money(cancel.data.refundDueCents);
    return (
      <Modal
        open
        title="Layaway canceled"
        onClose={onClose}
        primaryAction={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        {cancel.data.refundDueCents === 0 ? (
          <Callout tone="info">Nothing to give back. The goods are back on sale.</Callout>
        ) : cancel.data.refundKind === 'cash' ? (
          <Callout tone="warning" title={`Give ${handed} back in cash`}>
            It is recorded as coming out of this device&apos;s drawer, so the count still balances.
            The goods are back on sale.
          </Callout>
        ) : cancel.data.refundKind === 'card' ? (
          cards.enabled ? (
            <Callout tone="success" title={`${handed} refunded to their card`}>
              It went back to the cards the layaway was paid with. The goods are back on sale.
            </Callout>
          ) : (
            <Callout tone="warning" title={`Refund ${handed} to their card`}>
              Put the refund through on the card terminal now. The goods are back on sale.
            </Callout>
          )
        ) : (
          <Callout tone="success" title={`${handed} added to their store credit`}>
            It is on their account for the next visit. The goods are back on sale.
          </Callout>
        )}
      </Modal>
    );
  }

  return (
    <>
      <Modal
        open
        title="Cancel this layaway?"
        description="The goods go back on sale and the customer gets back what they paid, less the store's cancellation fee."
        onClose={onClose}
        secondaryAction={
          <Button variant="ghost" onClick={onClose}>
            Keep the layaway
          </Button>
        }
        primaryAction={
          <Button
            variant="danger"
            busy={cancel.isPending}
            disabledReason={
              quote.data === undefined
                ? 'Loading what was paid'
                : registerId === null
                  ? 'This device is not set to a register yet'
                  : undefined
            }
            onClick={() => cancel.mutate(undefined)}
          >
            Cancel and refund {money(refundCents)}
          </Button>
        }
      >
        {quote.isError ? <Callout tone="danger">{errorMessage(quote.error)}</Callout> : null}
        {quote.data === undefined ? (
          <Loading label="Loading what was paid" />
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Paid</th>
                  <th>How</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {quote.data.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{new Date(payment.occurredAt).toLocaleDateString()}</td>
                    <td>{TENDER_LABELS[payment.kind]}</td>
                    <td className="num money">{money(payment.amountCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <dl className="figures">
              <div>
                <dt>Paid in all</dt>
                <dd className="money">{money(quote.data.paidCents)}</dd>
              </div>
              <div>
                <dt>Cancellation fee</dt>
                <dd className="money">{waiveFee ? 'waived' : money(quote.data.storeFeeCents)}</dd>
              </div>
              <div>
                <dt>Refund</dt>
                <dd className="money">{money(refundCents)}</dd>
              </div>
            </dl>
            {quote.data.storeFeeCents > 0 ? (
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={waiveFee}
                  onChange={(event) => setWaiveFee(event.target.checked)}
                />
                <span>Waive the fee</span>
              </label>
            ) : null}
            <div className="field">
              <span className="field__label">Give it back as</span>
              <div className="chips">
                {(['cash', 'card', 'store_credit'] as const).map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    className={`chip${refundKind === entry ? ' chip--on' : ''}`}
                    aria-pressed={refundKind === entry}
                    onClick={() => setRefundKind(entry)}
                  >
                    {TENDER_LABELS[entry]}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
        {cancel.isError && !isOverridable(cancel.error) ? (
          <Callout tone="danger">{errorMessage(cancel.error)}</Callout>
        ) : null}
      </Modal>
      {needsApproval ? (
        <ManagerApproval
          title="Manager approval needed"
          because="Canceling a layaway puts the goods back on sale and gives the customer's money back."
          busy={cancel.isPending}
          error={cancel.isError ? approvalRefusal(cancel.error) : null}
          onCancel={() => setNeedsApproval(false)}
          onApprove={(override) => cancel.mutate(override)}
        />
      ) : null}
    </>
  );
}

function LayawayPaymentDialog({ saleId, onClose }: { saleId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState<string | null>(null);
  const [kind, setKind] = useState<'cash' | 'card'>('cash');
  const [cash, setCash] = useState('');
  const [done, setDone] = useState<{ balanceCents: number; changeCents: number } | null>(null);
  /*
   * An instalment by card through BlueSage goes to the terminal (decision 230):
   * the payment is recorded when the card is approved, and this dialog says
   * so. The moment it opened, so an older approval is not mistaken for this one.
   */
  const cards = useRegisterCards();
  const onTerminal = cards.enabled && kind === 'card';
  const [openedAt] = useState(() => Date.now());
  const instalment = useCardAttempt(saleId, onTerminal);
  useEffect(() => {
    const attempt = instalment.data;
    if (attempt == null || done !== null) return;
    if (attempt.status !== 'approved' || !attempt.recorded) return;
    if (new Date(attempt.startedAt).getTime() < openedAt) return;
    void api.register.layaway.query({ saleId }).then((fresh) => {
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['register', 'layaway', saleId] });
      setDone({ balanceCents: fresh?.layaway.balanceCents ?? 0, changeCents: 0 });
    });
  }, [instalment.data, done, openedAt, saleId, queryClient]);

  const layaway = useQuery({
    queryKey: ['register', 'layaway', saleId],
    queryFn: () => api.register.layaway.query({ saleId }),
  });
  // The money goes into this till's drawer (2026-09-25).
  const stations = useQuery({
    queryKey: ['register', 'stations'],
    queryFn: () => api.register.stations.query(),
  });
  const registerId = stations.data?.till.registerId ?? null;

  const balanceCents = layaway.data?.layaway.balanceCents ?? 0;
  const shown = amount ?? (balanceCents / 100).toFixed(2);
  const parsed = tryParseMoney(shown);

  // Same distinction as the deposit: what goes on the layaway, against what was
  // put in the operator's hand.
  const handed = kind === 'cash' && cash.trim() !== '' ? tryParseMoney(cash) : null;
  const changeCents = handed === null || parsed === null ? 0 : Math.max(handed - parsed, 0);
  const shortCash = handed !== null && parsed !== null && handed < parsed;

  const pay = useMutation({
    mutationFn: () =>
      api.register.layawayPayment.mutate({
        saleId,
        registerId: registerId!,
        kind,
        amountCents: parsed ?? 0,
        cashTenderedCents: handed,
      }),
    onSuccess: (result) => {
      recordBreadcrumb('sales', `took a layaway payment — ${result.balanceCents} left`);
      void queryClient.invalidateQueries({ queryKey: ['sales'] });
      void queryClient.invalidateQueries({ queryKey: ['register', 'layaway', saleId] });
      setDone({ balanceCents: result.balanceCents, changeCents: result.changeCents });
    },
  });

  if (done !== null) {
    return (
      <Modal
        open
        title={done.balanceCents === 0 ? 'Layaway paid off' : 'Payment taken'}
        onClose={onClose}
        primaryAction={
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <Callout tone={done.balanceCents === 0 ? 'success' : 'info'}>
          {done.balanceCents === 0
            ? 'Paid in full. The goods are released and the sale is complete — hand them over.'
            : `${money(done.balanceCents)} still to pay.`}
        </Callout>
        {/* Money going back out of the drawer is never silent. */}
        {done.changeCents > 0 ? (
          <Callout tone="warning" title={`Give ${money(done.changeCents)} change`}>
            The installment is on the layaway; this is the rest of what they handed over.
          </Callout>
        ) : null}
      </Modal>
    );
  }

  return (
    <Modal
      open
      title="Take a layaway payment"
      description={
        layaway.data == null ? undefined : `${money(balanceCents)} outstanding on this layaway.`
      }
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        onTerminal ? undefined : (
          <Button
            variant="primary"
            busy={pay.isPending}
            disabledReason={
              layaway.data == null
                ? 'Loading the layaway'
                : registerId === null
                  ? 'This device is not set to a register yet'
                  : parsed === null || parsed <= 0
                    ? 'Enter how much they are paying'
                    : parsed > balanceCents
                      ? 'That is more than is outstanding'
                      : shortCash
                        ? 'That is less cash than the payment'
                        : undefined
            }
            onClick={() => pay.mutate()}
          >
            Take {parsed === null ? '' : money(parsed)}
          </Button>
        )
      }
    >
      {layaway.data == null ? (
        <Loading label="Loading the layaway" />
      ) : (
        <>
          <div className="field">
            <label className="field__label" htmlFor="layaway-payment">
              Payment
            </label>
            <input
              id="layaway-payment"
              className="input money"
              inputMode="decimal"
              autoFocus
              value={shown}
              onChange={(event) => setAmount(event.target.value)}
            />
            <p className="field__hint">
              {parsed === null || parsed >= balanceCents
                ? 'Pays the layaway off in full.'
                : `${money(balanceCents - parsed)} would be left.`}
            </p>
          </div>

          <div className="field">
            <span className="field__label">Paid by</span>
            <div className="chips">
              {(['cash', 'card'] as const).map((entry) => (
                <button
                  key={entry}
                  type="button"
                  className={`chip${kind === entry ? ' chip--on' : ''}`}
                  aria-pressed={kind === entry}
                  onClick={() => setKind(entry)}
                >
                  {TENDER_LABELS[entry]}
                </button>
              ))}
            </div>
          </div>

          {kind === 'cash' ? (
            <div className="field">
              <label className="field__label" htmlFor="layaway-payment-cash">
                Cash handed over
              </label>
              <input
                id="layaway-payment-cash"
                className="input money"
                inputMode="decimal"
                placeholder={parsed === null ? '0.00' : (parsed / 100).toFixed(2)}
                value={cash}
                onChange={(event) => setCash(event.target.value)}
              />
              <p className="field__hint">
                {shortCash
                  ? 'That is less than the payment.'
                  : changeCents > 0
                    ? `Change ${money(changeCents)}.`
                    : 'Leave blank if they hand over the exact amount.'}
              </p>
            </div>
          ) : null}

          {onTerminal &&
          registerId !== null &&
          parsed !== null &&
          parsed > 0 &&
          parsed <= balanceCents ? (
            <TerminalCardPanel
              saleId={saleId}
              registerId={registerId}
              dueCents={parsed}
              isRefund={false}
              sandbox={cards.sandbox}
              onSaleChanged={() => void instalment.refetch()}
              onCompleted={() => undefined}
            />
          ) : null}

          {layaway.data.payments.length > 0 ? (
            <div className="field">
              <span className="field__label">Paid so far</span>
              <ul className="receipt__tenders">
                {layaway.data.payments.map((payment) => (
                  <li key={payment.id} className="receipt__tender">
                    <span>
                      {TENDER_LABELS[payment.kind] ?? payment.kind} ·{' '}
                      {new Date(payment.occurredAt).toLocaleDateString()}
                    </span>
                    <span className="money">{money(payment.amountCents)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {pay.isError ? <Callout tone="danger">{errorMessage(pay.error)}</Callout> : null}
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Send the receipt. Pre-filled from the customer on file, if there is one.
 *
 * Exported because the register needs the identical dialog the moment a sale
 * completes — which is when the customer is actually standing there to give you
 * their address, rather than a day later on the Sales screen.
 */
export function EmailReceiptDialog({
  saleId,
  defaultEmail,
  attachedCustomerId = null,
  onClose,
}: {
  saleId: string;
  defaultEmail: string;
  /** Already on the sale, if anyone is — then there is nothing to look up. */
  attachedCustomerId?: string | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(defaultEmail);
  const [debounced, setDebounced] = useState(defaultEmail);
  const [creating, setCreating] = useState(false);
  const [sent, setSent] = useState(false);

  const address = email.trim().toLowerCase();
  const looksLikeEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(address), 200);
    return () => clearTimeout(timer);
  }, [address]);

  /*
   * The address is a *lookup*, not just a destination (SCOPE §9.1).
   *
   * Sending to a typed address and moving on left the receipt reachable only
   * by the number on it — so the customer who comes back in March saying "you
   * emailed it to me" could not be helped, which is the whole reason for
   * emailing it. Matching the address to a person is what makes a no-receipt
   * return findable, and it is also how the address stops being retyped every
   * visit.
   */
  const match = useQuery({
    queryKey: ['customers', 'search', debounced],
    enabled: attachedCustomerId === null && debounced.length > 3 && debounced.includes('@'),
    queryFn: () => api.customers.search.query({ term: debounced, limit: 5 }),
  });

  const found =
    attachedCustomerId !== null
      ? null
      : ((match.data ?? []).find((person) => person.email?.toLowerCase() === debounced) ?? null);

  const searching = match.isFetching && attachedCustomerId === null;
  const known = attachedCustomerId !== null || found !== null;

  const send = useMutation({
    mutationFn: async () => {
      // Attach first: the sale is what carries the customer, and sending
      // without it would leave the same unfindable receipt as before.
      if (attachedCustomerId === null && found !== null) {
        await api.sales.attachCustomer.mutate({ saleId, customerId: found.id });
      }
      return api.sales.emailReceipt.mutate({ saleId, email: address });
    },
    onSuccess: () => {
      recordBreadcrumb('sales', 'emailed a receipt');
      void queryClient.invalidateQueries({ queryKey: ['sales', 'detail', saleId] });
      setSent(true);
    },
  });

  if (creating) {
    return (
      <NewCustomerDialog
        term={address}
        onClose={() => setCreating(false)}
        onCreated={async (customerId) => {
          await api.sales.attachCustomer.mutate({ saleId, customerId });
          void queryClient.invalidateQueries({ queryKey: ['sales', 'detail', saleId] });
          setCreating(false);
          send.mutate();
        }}
      />
    );
  }

  return (
    <Modal
      open
      title={sent ? 'Receipt sent' : 'Email this receipt'}
      description={
        sent ? undefined : 'It goes out from the store, and replies come back to the shop.'
      }
      onClose={onClose}
      secondaryAction={
        sent ? null : (
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        )
      }
      primaryAction={
        sent ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : !looksLikeEmail || known ? (
          <Button
            variant="primary"
            busy={send.isPending}
            disabledReason={
              !looksLikeEmail
                ? 'Enter an email address'
                : searching
                  ? 'Checking who this is'
                  : undefined
            }
            onClick={() => send.mutate()}
          >
            Send
          </Button>
        ) : (
          /*
           * No match, so the honest next step is making the record rather than
           * sending into the dark. The address is carried across, so the whole
           * cost of doing it properly is one press (§2.2-11).
           */
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add customer and send
          </Button>
        )
      }
    >
      {sent ? (
        <Callout tone="success">Sent to {address}.</Callout>
      ) : (
        <>
          <input
            className="input"
            type="email"
            autoFocus
            inputMode="email"
            autoComplete="off"
            placeholder="customer@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && looksLikeEmail && known) {
                event.preventDefault();
                send.mutate();
              }
            }}
          />

          {/*
            Domain suggestions here more than anywhere: this is typed at the
            counter with the customer waiting, and an emailed receipt is what
            makes a no-receipt return findable months later (decision 74). The
            step people skip is the one that costs ten keystrokes.
          */}
          {emailDomainSuggestions(email).length > 0 ? (
            <div className="chips quick-add__domains">
              {emailDomainSuggestions(email).map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="chip"
                  onClick={() => setEmail(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          ) : null}

          {attachedCustomerId !== null ? (
            <p className="text-secondary">This sale is already on a customer&rsquo;s record.</p>
          ) : !looksLikeEmail ? null : searching ? (
            <p className="text-secondary">Checking who this is…</p>
          ) : found !== null ? (
            <Callout tone="info">
              {customerName(found)} — the sale goes on their record, so it can be found again
              without the receipt.
            </Callout>
          ) : (
            <Callout tone="warning">
              Nobody on file has this address. Adding them keeps the sale findable in March when
              they come back without the receipt.
            </Callout>
          )}

          {send.isError ? <Callout tone="danger">{errorMessage(send.error)}</Callout> : null}
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

/**
 * Choosing what is coming back.
 *
 * Default is everything still returnable, because "the customer brought the
 * whole thing back" is the common case (§2.2-5, smart defaults). Damaged goods
 * are one checkbox away — they get refunded but never go back on the floor.
 */
function ReturnDialog({
  saleId,
  saleNumber,
  lines,
  onClose,
  onStarted,
}: {
  saleId: string;
  saleNumber: string | null;
  lines: {
    id: string;
    description: string;
    quantity: number;
    returnableQuantity: number;
    netCents: number;
    /** A consigned item: final sale, and its return wants a manager (decision 139). */
    consignmentItemId: string | null;
  }[];
  onClose: () => void;
  onStarted: () => void;
}) {
  const queryClient = useQueryClient();
  const returnable = lines.filter((line) => line.returnableQuantity > 0);
  /**
   * Why a manager is being asked: the store's return policy (the server says
   * so when it refuses), or a consigned item, which the screen knows before
   * it asks — its owner has already been paid, so it is final sale.
   */
  const [approvalFor, setApprovalFor] = useState<'policy' | 'consignment' | null>(null);
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.auth.me.query() });
  // The same gate as issuing credit by hand, because that is what undoing it is.
  const canTakeBackConsignment =
    me.data?.signedIn === true && me.data.permissions.includes('customer.store_credit');
  /*
   * The receipt gate (item 13b). A return starts by proving the paper is in
   * hand: the scanned barcode is the sale number, so the scan either matches
   * this sale or it is the wrong receipt. "No receipt" is never a dead end —
   * it goes through the same manager approval the out-of-window path uses,
   * and the server decides whether the store's policy demands it.
   */
  const [receipt, setReceipt] = useState<'asking' | { scanned: string | null }>(
    saleNumber === null ? { scanned: null } : 'asking',
  );
  const [scanDraft, setScanDraft] = useState('');
  const scannedNumber = receipt === 'asking' ? null : receipt.scanned;
  const wrongReceipt =
    receipt === 'asking' &&
    scanDraft.trim() !== '' &&
    saleNumber !== null &&
    scanDraft.trim().toUpperCase() !== saleNumber.toUpperCase();

  // Everything returnable by default — except a consigned item, which is final
  // sale and has to be chosen on purpose.
  const [selection, setSelection] = useState(() =>
    Object.fromEntries(
      returnable.map((line) => [
        line.id,
        { quantity: line.consignmentItemId === null ? line.returnableQuantity : 0, restock: true },
      ]),
    ),
  );
  const consignmentChosen = returnable.some(
    (line) => line.consignmentItemId !== null && (selection[line.id]?.quantity ?? 0) > 0,
  );

  const start = useMutation({
    mutationFn: async (override?: ManagerOverride) => {
      const stations = await api.register.stations.query();
      const registerId = stations.till.registerId;
      if (registerId === null) {
        throw new Error(
          'This device is not set to a register yet. An admin sets it in Settings, under Devices.',
        );
      }

      const refund = await api.register.startReturn.mutate({
        registerId,
        originalSaleId: saleId,
        ...(scannedNumber === null ? {} : { receiptNumber: scannedNumber }),
        ...(override === undefined ? {} : { override }),
      });
      try {
        return await api.register.setReturnLines.mutate({
          saleId: refund.sale.id,
          lines: Object.entries(selection)
            .filter(([, value]) => value.quantity > 0)
            .map(([originalLineId, value]) => ({
              originalLineId,
              quantity: value.quantity,
              restock: value.restock,
            })),
          // One approval covers both questions: the policy and a consigned item.
          ...(override === undefined ? {} : { override }),
        });
      } catch (error) {
        // Refused (a consigned item wants a manager): do not leave the empty
        // refund on the register while the manager is asked — the retry opens
        // a fresh one.
        await api.register.void
          .mutate({ saleId: refund.sale.id, reason: 'Return waiting for a manager' })
          .catch(() => undefined);
        throw error;
      }
    },
    onSuccess: () => {
      recordBreadcrumb('sales', 'started a return');
      void queryClient.invalidateQueries({ queryKey: ['register'] });
      onStarted();
    },
    onError: (error) => {
      // Outside the tenant's return window a return is still allowed — it just
      // wants a manager (§2-6). Before this, the counter simply stopped.
      if (isOverridable(error)) setApprovalFor(consignmentChosen ? 'consignment' : 'policy');
    },
  });

  const chosen = Object.values(selection).filter((entry) => entry.quantity > 0).length;

  if (approvalFor !== null) {
    return (
      <ManagerApproval
        title="Manager approval needed"
        because={
          approvalFor === 'consignment'
            ? 'Consignment items are final sale. Its owner was paid in store credit when it sold, and taking it back takes that credit back — even if they have spent it.'
            : scannedNumber === null
              ? 'No receipt was scanned — or the sale is outside the return window. A manager signs off either way.'
              : 'This sale is outside the store’s return window.'
        }
        busy={start.isPending}
        error={start.isError ? approvalRefusal(start.error) : null}
        onCancel={onClose}
        onApprove={(override) => start.mutate(override)}
      />
    );
  }

  if (receipt === 'asking') {
    return (
      <Modal
        open
        title="Scan the receipt"
        description="A return starts with the paper in hand — the barcode at the foot of the receipt is this sale's number. No receipt is never a dead end; it just wants a manager."
        onClose={onClose}
        secondaryAction={
          <Button variant="ghost" onClick={() => setReceipt({ scanned: null })}>
            No receipt
          </Button>
        }
        primaryAction={
          <Button
            variant="primary"
            disabledReason={
              scanDraft.trim() === ''
                ? 'Scan or type the receipt number'
                : wrongReceipt
                  ? `That is not this sale's receipt (${saleNumber})`
                  : undefined
            }
            onClick={() => setReceipt({ scanned: scanDraft.trim() })}
          >
            Receipt in hand
          </Button>
        }
      >
        <label className="field">
          <span className="field__label">Receipt number</span>
          <input
            className="input"
            autoFocus
            value={scanDraft}
            onChange={(event) => setScanDraft(event.target.value)}
            onKeyDown={(event) => {
              // A scanner ends with Enter; the scan should land like a scan.
              if (event.key === 'Enter' && scanDraft.trim() !== '' && !wrongReceipt) {
                setReceipt({ scanned: scanDraft.trim() });
              }
            }}
            placeholder="S-1234"
          />
        </label>
        {wrongReceipt ? (
          <p className="text-secondary">
            That receipt belongs to a different sale — find it in the list and start the return from
            there, so the refund is worked out against what was actually paid on it.
          </p>
        ) : null}
      </Modal>
    );
  }

  return (
    <Modal
      open
      wide
      title="What is coming back?"
      description="The refund is worked out from what was actually paid on this receipt."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Back to receipt
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={start.isPending}
          disabledReason={chosen === 0 ? 'Choose at least one item' : undefined}
          onClick={() =>
            consignmentChosen && !canTakeBackConsignment
              ? setApprovalFor('consignment')
              : start.mutate(undefined)
          }
        >
          Take the return
        </Button>
      }
    >
      <ul className="return-lines">
        {returnable.map((line) => {
          const entry = selection[line.id]!;
          return (
            <li key={line.id} className="return-line">
              <div>
                <div>{line.description}</div>
                {line.consignmentItemId === null ? (
                  <div className="text-secondary">
                    {line.returnableQuantity} of {line.quantity} still returnable
                  </div>
                ) : (
                  <div className="return-line__final">
                    <Badge tone="warning">Final sale · consignment</Badge>
                    <span className="text-secondary">
                      {entry.quantity > 0
                        ? ' Its owner’s store credit is taken back with it.'
                        : ' Set the quantity to 1 to take it back — a manager approves.'}
                    </span>
                  </div>
                )}
              </div>

              <div className="return-line__controls">
                <label className="field__label" htmlFor={`qty-${line.id}`}>
                  Qty
                </label>
                <input
                  id={`qty-${line.id}`}
                  className="qty-input num"
                  inputMode="numeric"
                  value={String(entry.quantity)}
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, '');
                    const parsed = digits === '' ? 0 : Number.parseInt(digits, 10);
                    setSelection((current) => ({
                      ...current,
                      [line.id]: {
                        ...entry,
                        quantity: Math.min(parsed, line.returnableQuantity),
                      },
                    }));
                  }}
                />

                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={!entry.restock}
                    onChange={(event) =>
                      setSelection((current) => ({
                        ...current,
                        [line.id]: { ...entry, restock: !event.target.checked },
                      }))
                    }
                  />
                  <span>Damaged</span>
                </label>
              </div>
            </li>
          );
        })}
      </ul>

      {start.isError ? <Callout tone="danger">{errorMessage(start.error)}</Callout> : null}
    </Modal>
  );
}

/**
 * Put a customer on a sale that is already finished (SCOPE §9.1).
 *
 * The search is the same one the register uses, so nobody learns a second way
 * to find a person. What is different is the sentence underneath: this changes
 * who the sale belongs to and **nothing about the money**, which is exactly
 * what someone half-expects it to do when the customer turns out to be tax
 * exempt. Saying so before the press beats a surprise in November (§2.2-7).
 */
function AttachToCustomer({
  saleId,
  saleNumber,
}: {
  saleId: string;
  /** For the notice that says which sale went to whom. */
  saleNumber: string | null;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 120);
    return () => clearTimeout(timer);
  }, [term]);

  const results = useQuery({
    queryKey: ['customers', 'search', debounced],
    queryFn: () => api.customers.search.query({ term: debounced, limit: 8 }),
    enabled: open && debounced.length > 0,
  });

  const attach = useMutation({
    mutationFn: (customerId: string) => api.sales.attachCustomer.mutate({ saleId, customerId }),
    /*
     * Invalidate the whole `sales` root, not just the open dialog.
     *
     * Refetching only the detail left the search table behind it still showing
     * a dash in the Customer column, and the change appeared to have been lost
     * until the page was reloaded. Anything a mutation can be seen from has to
     * be told — and enumerating those places is a list that goes stale, so the
     * root is invalidated instead. `customers` too: the person's purchase
     * history and lifetime spend both just gained a sale.
     */
    onSuccess: async (_result, customerId) => {
      const chosen = results.data?.find((entry) => entry.id === customerId);
      const named = chosen === undefined ? 'the customer' : customerName(chosen);
      notify({
        title:
          saleNumber === null ? `Sale added to ${named}` : `Sale ${saleNumber} added to ${named}`,
        detail: 'The amounts charged did not change.',
      });
      setOpen(false);
      setTerm('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sales'] }),
        queryClient.invalidateQueries({ queryKey: ['customers'] }),
      ]);
    },
  });

  const found = results.data ?? [];

  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Add to a customer
      </Button>

      {open ? (
        <Modal
          open
          title="Whose sale was this?"
          description="Search by name, phone or email. This records who bought it — the amounts charged do not change."
          onClose={() => setOpen(false)}
          secondaryAction={
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Close
            </Button>
          }
        >
          <input
            className="search__input"
            value={term}
            autoFocus
            placeholder="Name, phone or email"
            onChange={(event) => setTerm(event.target.value)}
          />

          {attach.isError ? <Callout tone="danger">{errorMessage(attach.error)}</Callout> : null}

          {found.length === 0 && debounced.length > 0 && !results.isPending ? (
            <Callout tone="neutral">Nobody matches “{debounced}”.</Callout>
          ) : null}

          <div className="sales__customer-results">
            {found.map((customer) => (
              <button
                key={customer.id}
                type="button"
                className="link-button"
                disabled={attach.isPending}
                onClick={() => attach.mutate(customer.id)}
              >
                {`${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim() ||
                  (customer.companyName ?? 'Unnamed')}
                {customer.phone !== null ? ` · ${customer.phone}` : ''}
              </button>
            ))}
          </div>
        </Modal>
      ) : null}
    </>
  );
}
